/**
 * Item page → Planning & delivery → "What its types usually bring".
 *
 * Every type list the item has a type in, and what each type's pattern says
 * (docs/9-exploration/TYPE-PATTERNS-PRD.md): certainty, shape, workload, bar —
 * each assumption with the type it came from. Nothing has to win: where two
 * types disagree, the item's owner picks for this item (an override, with a
 * reason). Custom type lists are picked here; built-in lists (work, launch,
 * investment) are picked where the page already offers them.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Select, TextInput } from '@/components/ds';
import { btnPrimary, btnSecondary, LABEL_MONO, status } from '@/components/mono/primitives';
import { useUserStore } from '@/stores/prioritization/userStore';
import { recordItemFieldChange } from '@/stores/prioritization/itemsStore';
import { resourceTypeLabel } from '@/stores/prioritization/resourceTypesStore';
import { useTypeListPickers } from '@/hooks/prioritization/useTypeListPickers';
import {
  fetchItemCustomTypes,
  fetchItemOverrides,
  setItemCustomType,
  setItemPatternOverride,
} from '@/lib/typePatternsService';
import {
  ANCHOR_LABEL,
  DATE_FIRMNESS,
  DATE_FIRMNESS_LABEL,
  QUALITY_BARS,
  QUALITY_LABEL,
  resolveItemAssumptions,
  workloadTotals,
  type FieldAssumption,
} from '@/lib/typePatterns';
import type { ItemPatternOverride, PatternPart } from '@/types/prioritization/typePatterns';
import { DisclosureTriangle, OwnedSectionHeading } from './ItemDetailThirdPassView';
import { TypePatternDetail } from './TypePatternDetail';

interface Props {
  itemId: string;
  workspaceId: string | null;
  workType?: string | null;
  launchType?: string | null;
  investmentType?: string | null;
}

type Editable = 'date' | 'quality' | 'scheduleSlackWeeks' | 'confidenceCeiling';

const FIELD_META: Record<Editable, { part: PatternPart; label: string }> = {
  date: { part: 'bar', label: 'Date' },
  quality: { part: 'bar', label: 'Quality bar' },
  scheduleSlackWeeks: { part: 'certainty', label: 'Schedule slack' },
  confidenceCeiling: { part: 'certainty', label: 'Highest confidence' },
};

function show(field: Editable, v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (field === 'date') return DATE_FIRMNESS_LABEL[v as keyof typeof DATE_FIRMNESS_LABEL] ?? String(v);
  if (field === 'quality') return QUALITY_LABEL[v as keyof typeof QUALITY_LABEL] ?? String(v);
  if (field === 'scheduleSlackWeeks') return `${v} week${v === 1 ? '' : 's'}`;
  return `${Math.round(Number(v) * 100)}%`;
}

export function ItemPatternsSection({ itemId, workspaceId, workType, launchType, investmentType }: Props) {
  const can = useUserStore((s) => s.can);
  const mayEdit = can('items.edit');
  const { lists, patterns, typeLabel, workTypeFacts } = useTypeListPickers(workspaceId);

  const [custom, setCustom] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<ItemPatternOverride[]>([]);
  const [editing, setEditing] = useState<Editable | null>(null);
  const [draft, setDraft] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [openCustom, setOpenCustom] = useState<Set<string>>(() => new Set());

  const reload = useCallback(async () => {
    const [c, o] = await Promise.all([fetchItemCustomTypes(itemId), fetchItemOverrides(itemId)]);
    setCustom(c);
    setOverrides(o);
  }, [itemId]);

  useEffect(() => {
    void reload().catch(() => undefined);
  }, [reload]);

  const customLists = lists.filter((l) => !l.builtin && l.isActive);
  const keyById = useMemo(() => new Map(lists.map((l) => [l.id, l.key])), [lists]);

  const a = useMemo(
    () =>
      resolveItemAssumptions({
        lists,
        itemTypes: {
          work_type: workType,
          launch_type: launchType,
          investment_type: investmentType,
          ...Object.fromEntries(Object.entries(custom).map(([listId, v]) => [keyById.get(listId) ?? listId, v])),
        },
        patterns,
        overrides,
        typeLabel,
        workTypeFacts,
      }),
    // typeLabel / workTypeFacts change with lists and the work type catalog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lists, patterns, overrides, custom, keyById, workType, launchType, investmentType],
  );

  async function pickCustom(listId: string, value: string) {
    setError(null);
    const err = await setItemCustomType(itemId, listId, value || null);
    if (err) return setError(err);
    const list = lists.find((l) => l.id === listId);
    const label = (v: string | null | undefined) => (v ? list?.options.find((o) => o.value === v)?.label ?? v : null);
    recordItemFieldChange(itemId, 'customTypeList', { list: list?.name ?? null, type: label(custom[listId]) }, { list: list?.name ?? null, type: label(value || null) });
    await reload();
  }

  async function saveOverride(field: Editable, value: unknown) {
    setError(null);
    const err = await setItemPatternOverride({
      itemId,
      part: FIELD_META[field].part,
      field,
      value,
      reason: reason.trim() || null,
    });
    if (err) return setError(err);
    recordItemFieldChange(itemId, 'patternOverride', null, { part: FIELD_META[field].part, field, value, reason: reason.trim() || null });
    setEditing(null);
    setReason('');
    await reload();
  }

  const rows: Array<{ field: Editable; f: FieldAssumption<unknown> }> = [
    { field: 'date', f: a.date as FieldAssumption<unknown> },
    { field: 'quality', f: a.quality as FieldAssumption<unknown> },
    { field: 'scheduleSlackWeeks', f: a.slackWeeks as FieldAssumption<unknown> },
    { field: 'confidenceCeiling', f: a.confidenceCeiling as FieldAssumption<unknown> },
  ];
  const totals = [...workloadTotals(a.workload).entries()];
  const anyType = Boolean(workType || launchType || investmentType || Object.keys(custom).length);

  return (
    <div data-testid="item-patterns">
      {customLists.length > 0 && (
        <>
          <OwnedSectionHeading title="More ways it is classified" owned="lists your workspace added" />
          {customLists.map((l) => (
            <div key={l.id} className="border-b py-2.5" style={{ borderColor: 'var(--rule)' }}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex w-[170px] shrink-0 items-center gap-1 text-sm text-[var(--text-dim)]">
                  {custom[l.id] ? (
                    <DisclosureTriangle
                      open={openCustom.has(l.id)}
                      onClick={() =>
                        setOpenCustom((prev) => {
                          const next = new Set(prev);
                          if (next.has(l.id)) next.delete(l.id);
                          else next.add(l.id);
                          return next;
                        })
                      }
                      label={`${l.name} details`}
                    />
                  ) : (
                    <span className="inline-block w-5 shrink-0" />
                  )}
                  {l.name}
                </span>
                {mayEdit ? (
                  <div className="w-64">
                    <Select
                      size="sm"
                      aria-label={l.name}
                      value={custom[l.id] ?? ''}
                      placeholder="Not set"
                      onChange={(e) => void pickCustom(l.id, e.target.value)}
                      options={l.options.filter((o) => o.isActive).map((o) => ({ value: o.value, label: o.label }))}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-[var(--text-sec)]">
                    {custom[l.id] ? typeLabel(l.key, custom[l.id]) : 'Not set'}
                  </span>
                )}
              </div>
              {custom[l.id] && openCustom.has(l.id) && (
                <div className="mt-2 pl-6">
                  <TypePatternDetail listKey={l.key} value={custom[l.id]} workspaceId={workspaceId} />
                </div>
              )}
            </div>
          ))}
        </>
      )}

      <OwnedSectionHeading title="What its types add up to" owned="each assumption names its type · this item can differ" />

      {!anyType && (
        <p className="mt-3 text-sm text-[var(--text-ter)]">
          No types set, so no patterns apply. Set a work, launch or investment type above.
        </p>
      )}
      {anyType && a.empty && (
        <p className="mt-3 text-sm text-[var(--text-ter)]">
          None of this item's types has a pattern yet.{' '}
          <Link to="/prioritization/settings/type-patterns" className="underline">Type Lists &amp; Patterns</Link>
        </p>
      )}

      {!a.empty && (
        <div className="mt-3 divide-y divide-[var(--rule)]">
          {rows
            .filter(({ f }) => f.stated.length > 0 || f.override)
            .map(({ field, f }) => (
              <div key={field} className="py-2.5" data-testid={`pattern-field-${field}`}>
                <div className="grid grid-cols-1 items-baseline gap-x-4 gap-y-1 sm:grid-cols-[10rem_minmax(0,1fr)_auto]">
                  <span className={LABEL_MONO}>{FIELD_META[field].label}</span>
                  <span className="text-sm text-[var(--text-pri)]">
                    {show(field, f.effective)}
                    {f.override && (
                      <span className="ml-2 text-xs text-[var(--text-ter)]">
                        this item{f.override.reason ? ` — ${f.override.reason}` : ''}; the types say{' '}
                        {f.stated.map((s) => `${show(field, s.value)} (${s.source.typeLabel})`).join(', ') || 'nothing'}
                      </span>
                    )}
                    {!f.override && (
                      <span className="ml-2 text-xs text-[var(--text-ter)]">
                        {f.stated.map((s) => `${show(field, s.value)} from ${s.source.typeLabel}`).join(' · ')}
                      </span>
                    )}
                    {f.needsChoice && (
                      <span className="ml-2 inline-block" style={{ verticalAlign: 'baseline' }}>
                        <span {...status('mustard')}>
                        Types disagree — pick for this item
                        </span>
                      </span>
                    )}
                  </span>
                  {mayEdit && editing !== field && (
                    <span className="flex gap-2">
                      <button type="button" {...btnSecondary('text-xs')} onClick={() => { setEditing(field); setDraft(''); }}>
                        {f.override ? 'Change' : f.needsChoice ? 'Pick' : 'Differs here'}
                      </button>
                      {f.override && (
                        <button type="button" {...btnSecondary('text-xs')} onClick={() => void saveOverride(field, null)}>
                          Use the types'
                        </button>
                      )}
                    </span>
                  )}
                </div>
                {editing === field && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <div className="w-44">
                      {field === 'date' || field === 'quality' ? (
                        <Select
                          size="sm"
                          aria-label={FIELD_META[field].label}
                          value={draft}
                          placeholder="Choose…"
                          onChange={(e) => setDraft(e.target.value)}
                          options={(field === 'date' ? DATE_FIRMNESS : QUALITY_BARS).map((v) => ({
                            value: v,
                            label: field === 'date' ? DATE_FIRMNESS_LABEL[v as keyof typeof DATE_FIRMNESS_LABEL] : QUALITY_LABEL[v as keyof typeof QUALITY_LABEL],
                          }))}
                        />
                      ) : (
                        <TextInput
                          size="sm"
                          aria-label={FIELD_META[field].label}
                          inputMode="numeric"
                          value={draft}
                          placeholder={field === 'scheduleSlackWeeks' ? 'weeks' : '%'}
                          onChange={(e) => setDraft(e.target.value)}
                        />
                      )}
                    </div>
                    <div className="min-w-[14rem] flex-1">
                      <TextInput size="sm" aria-label="Why" value={reason} placeholder="Why this item differs" onChange={(e) => setReason(e.target.value)} />
                    </div>
                    <button type="button" {...btnSecondary('text-xs')} onClick={() => setEditing(null)}>Cancel</button>
                    <button
                      type="button"
                      {...btnPrimary('text-xs disabled:opacity-50')}
                      disabled={!draft.trim()}
                      onClick={() => {
                        const v =
                          field === 'date' || field === 'quality'
                            ? draft
                            : field === 'confidenceCeiling'
                              ? Math.min(100, Math.max(0, Number(draft))) / 100
                              : Math.max(0, Number(draft));
                        void saveOverride(field, v);
                      }}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            ))}

          {a.approach.stated.length > 0 && (
            <div className="grid grid-cols-1 gap-x-4 py-2.5 sm:grid-cols-[10rem_minmax(0,1fr)]">
              <span className={LABEL_MONO}>Approach</span>
              <span className="text-sm text-[var(--text-pri)]">
                {a.approach.stated.map((s) => `${s.value} (${s.source.typeLabel})`).join(' · ')}
              </span>
            </div>
          )}

          {(a.beforeScheduling.length > 0 || a.signOff.length > 0) && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 py-2.5 sm:grid-cols-[10rem_minmax(0,1fr)]">
              {a.beforeScheduling.length > 0 && (
                <>
                  <span className={LABEL_MONO}>Before scheduling</span>
                  <ul className="text-sm text-[var(--text-pri)]">
                    {a.beforeScheduling.map((b, i) => (
                      <li key={i}>{b.text} <span className="text-xs text-[var(--text-ter)]">— {b.source.typeLabel}</span></li>
                    ))}
                  </ul>
                </>
              )}
              {a.signOff.length > 0 && (
                <>
                  <span className={LABEL_MONO}>Signs off</span>
                  <ul className="text-sm text-[var(--text-pri)]">
                    {a.signOff.map((b, i) => (
                      <li key={i}>{b.text} <span className="text-xs text-[var(--text-ter)]">— {b.source.typeLabel}</span></li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {a.shapes.length > 0 && (
            <div className="grid grid-cols-1 gap-x-4 py-2.5 sm:grid-cols-[10rem_minmax(0,1fr)]">
              <span className={LABEL_MONO}>Shape</span>
              <div className="space-y-1 text-sm text-[var(--text-pri)]">
                {a.shapes.map((s, i) => (
                  <div key={i}>
                    {s.stages.map((st) => `${st.label} (${st.weeks} wk)`).join(' → ')}
                    <span className="ml-2 text-xs text-[var(--text-ter)]">
                      {s.source.typeLabel}, from {ANCHOR_LABEL[s.anchor]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {a.workload.length > 0 && (
            <div className="grid grid-cols-1 gap-x-4 py-2.5 sm:grid-cols-[10rem_minmax(0,1fr)]">
              <span className={LABEL_MONO}>Workload</span>
              <div className="space-y-1 text-sm text-[var(--text-pri)]">
                {a.workload.map((l, i) => (
                  <div key={i}>
                    {resourceTypeLabel(l.resourceType)}: {l.capacityUnits} CU
                    {l.effortMultiplier !== 1 ? ` ×${l.effortMultiplier}` : ''}, weeks {l.fromWeek} to {l.toWeek} from{' '}
                    {ANCHOR_LABEL[l.anchor]}
                    <span className="ml-2 text-xs text-[var(--text-ter)]">{l.source.typeLabel}</span>
                  </div>
                ))}
                <div className="pt-1 text-xs text-[var(--text-sec)]">
                  Rough cut: {totals.map(([t, cu]) => `${resourceTypeLabel(t)} ${Math.round(cu * 10) / 10} CU`).join(', ')}. Each line keeps its
                  own type's multiplier.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-[var(--coral)]">{error}</p>}
    </div>
  );
}
